import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';
import Papa from 'papaparse';
import { toCamelCase as rToCamelCase } from 'remeda';

import { client, HttpException, type components } from '@/libs/openapi';
import { Resource } from '@/types/resource.type';

export interface PerformancePoint {
  x: number; // Unix timestamp ms
  y: number;
}

export interface PayoutGroup {
  kind: 'group';
  id: string;
  depth: number;
  name: string;
  cells: (string | null)[];
  children: PayoutTreeNode[];
}

export interface PayoutLeaf {
  kind: 'leaf';
  id: string;
  depth: number;
  date: string;
  cells: (string | null)[];
}

export type PayoutTreeNode = PayoutGroup | PayoutLeaf;

export interface PayoutContentData {
  columns: { id: string; name: string }[];
  grandTotal: (string | null)[] | null;
  tree: PayoutGroup[];
}

export interface PerformanceContentData {
  columns: { id: string; name: string; index: number }[];
  rows: { index: number; value: (string | null)[] }[];
  series: {
    totalTrafficLift: PerformancePoint[];
    widgetUsers: PerformancePoint[];
    additionalPageViews: PerformancePoint[];
  };
}

export const reportQueries = queryNamespace(Resource.report, {
  types: queryOptions({
    queryKey: [],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/report-types', { signal })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data.map((item) => ({ original: item }));
        }),
  }),

  list: (params: { type: string }) => queryOptions({
    queryKey: [ params ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/reports', { signal, params: { query: { type: params.type }}})
        .then(({ data, error, response }) => {
          if (error) {
            if (response.status === 404) {
              return [];
            }
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
  }),

  payoutContent: (params: {
    type: string;
    entityType: components['schemas']['ReportEntityTypeEnum'];
    fileName: string;
  }) => queryOptions({
    queryKey: [ 'payout-content', params ],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await client.GET('/customer/api/tcd/reports/files', {
        params: { query: { type: params.type, entityType: params.entityType, fileName: params.fileName }},
        parseAs: 'text',
        signal: signal,
      });

      if (error) {
        throw new HttpException({
          message: `Failed to fetch report file: ${response.statusText}`,
          response,
          isGlobal: false,
        });
      }

      const result = Papa.parse<string[]>(data, { delimiter: '\t', skipEmptyLines: true });
      const rawRows = result.data;

      // Need at least a header row and one data row.
      if (rawRows.length < 2) {
        return { columns: [], grandTotal: null, tree: [] };
      }

      const columns = [
        { id: 'col-website', name: 'Website' },
        { id: 'col-date', name: 'Date' },
        { id: 'col-usd', name: 'USD earnings' },
        { id: 'col-credits', name: 'Credits earned' },
        { id: 'col-avg-cost', name: 'Avg. cost per credit' },
        { id: 'col-pageviews', name: 'Pageviews' },
        { id: 'col-rpm', name: 'RPM' },
        { id: 'col-outbound-clicks', name: 'Outbound clicks' },
        { id: 'col-ctr', name: 'CTR' },
      ];

      const tree: PayoutGroup[] = [];
      let grandTotal: (string | null)[] | null = null;
      let currentWebsite: PayoutGroup | null = null;
      let currentWidget: PayoutGroup | null = null;
      let leafCounter = 0;

      const cell = (v: string | undefined): string | null => (v === '' || v == null ? null : v);

      // Start from index 1 — row 0 is the header.
      for (let i = 1; i < rawRows.length; i++) {
        const row = rawRows[i];
        const col0 = (row[0] ?? '').trim();
        const col1 = (row[1] ?? '').trim();
        const col2 = (row[2] ?? '').trim();

        // Capture the Grand Total summary row.
        if (col0 === 'Grand Total') {
          grandTotal = [
            null, // Website
            null, // Date
            cell(row[3]), // USD earnings
            cell(row[4]), // Credits earned
            cell(row[5]), // Avg. cost per credit
            cell(row[6]), // Pageviews
            cell(row[7]), // RPM
            cell(row[8]), // Outbound clicks
            cell(row[9]), // CTR
          ];
          continue;
        }

        // Website group: col[0]=name, col[1]="Total"
        const isWebsiteGroup = col0 !== '' && col1 === 'Total';
        // Widget group: col[0]="", col[1]=name, col[2]="Total"
        const isWidgetGroup = col0 === '' && col1 !== '' && col2 === 'Total';

        if (isWebsiteGroup) {
          const cells: (string | null)[] = [
            col0, // Website name
            null, // Date
            cell(row[3]), // USD earnings
            cell(row[4]), // Credits earned
            cell(row[5]), // Avg. cost per credit
            cell(row[6]), // Pageviews
            cell(row[7]), // RPM
            cell(row[8]), // Outbound clicks
            cell(row[9]), // CTR
          ];

          const id = `group-0-${col0}-${i}`;
          const group: PayoutGroup = { kind: 'group', id, depth: 0, name: col0, cells, children: [] };

          currentWebsite = group;
          currentWidget = null;
          tree.push(group);
        } else if (isWidgetGroup) {
          const cells: (string | null)[] = [
            col1, // Widget name
            null, // Date
            cell(row[3]), // USD earnings
            cell(row[4]), // Credits earned
            cell(row[5]), // Avg. cost per credit
            cell(row[6]), // Pageviews
            cell(row[7]), // RPM
            cell(row[8]), // Outbound clicks
            cell(row[9]), // CTR
          ];

          const id = `group-1-${col1}-${i}`;
          const group: PayoutGroup = { kind: 'group', id, depth: 1, name: col1, cells, children: [] };

          currentWidget = group;
          currentWebsite?.children.push(group);
        } else {
          // Leaf (date) row: col[0]="", col[1]="", col[2]=date
          const cells: (string | null)[] = [
            null, // Website
            col2, // Date
            cell(row[3]), // USD earnings
            cell(row[4]), // Credits earned
            cell(row[5]), // Avg. cost per credit
            cell(row[6]), // Pageviews
            cell(row[7]), // RPM
            cell(row[8]), // Outbound clicks
            cell(row[9]), // CTR
          ];

          const id = `leaf-${leafCounter++}`;
          const depth = currentWidget ? 2 : currentWebsite ? 1 : 0;
          const leaf: PayoutLeaf = { kind: 'leaf', id, depth, date: col2, cells };

          const parent = currentWidget ?? currentWebsite;
          if (parent) {
            parent.children.push(leaf);
          }
        }
      }

      return { columns, grandTotal, tree };
    },
  }),

  content: (params: {
    type: string;
    entityType: components['schemas']['ReportEntityTypeEnum'];
    fileName: string;
  }) => queryOptions({
    queryKey: [ params ],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await client.GET('/customer/api/tcd/reports/files', {
        params: { query: { type: params.type, entityType: params.entityType, fileName: params.fileName }},
        parseAs: 'text',
        signal: signal,
      });

      if (error) {
        throw new HttpException({
          message: `Failed to fetch report file: ${response.statusText}`,
          response,
          isGlobal: false,
        });
      }

      const empty: PerformanceContentData = { columns: [], rows: [], series: { totalTrafficLift: [], widgetUsers: [], additionalPageViews: [] }};

      let columns: PerformanceContentData['columns'] = [];
      let rows: PerformanceContentData['rows'] = [];
      let series: PerformanceContentData['series'] = { totalTrafficLift: [], widgetUsers: [], additionalPageViews: [] };

      // The CSV is tab-separated and row-oriented:
      //   Row 0 (header): [blank] | Grand Total | August 2024 | September 2024 | ...
      //   Rows 1+:        <metric name> | <grand total value> | <aug value> | <sep value> | ...
      const parseResult = Papa.parse<string[]>(data.trim(), {
        delimiter: '\t',
        skipEmptyLines: true,
        header: false,
        complete: (results) => {
          if (!results.data.length) {
            return;
          }

          const headerRow = [ ...results.data[0] ];
          const maxColumns = Math.max(...results.data.map((row) => row.length));

          // Most Tableau reports omit the first header column label.
          if (headerRow.length < maxColumns) {
            headerRow.unshift(...new Array(maxColumns - headerRow.length).fill(''));
          }

          columns = headerRow.map((name, index) => ({ id: String(index), index, name }));
          rows = results.data.slice(1).map((value, index) => ({ index, value }));

          // Convert month headers ("August 2024", "September 2024", …) to end-of-month timestamps.
          // Column 0 = row label, column 1 = Grand Total (skipped for charts), columns 2+ = monthly data.
          const xAxisDates = headerRow.slice(2).map((header) => {
            const date = new Date(header);
            return new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime();
          });

          // Maps camelCased row labels to chart series.
          // "Inbound non-sponsored clicks" and "Outbound sponsored clicks" both contribute to widgetUsers.
          const keyToCategoryMap = new Map<string, keyof typeof series>([
            [ 'totalTrafficLift', 'totalTrafficLift' ],
            [ 'additionalPageViews', 'additionalPageViews' ],
            [ 'inboundNonSponsoredClicks', 'widgetUsers' ],
            [ 'outboundSponsoredClicks', 'widgetUsers' ],
          ]);

          series = rows.reduce(
            (acc, { value }) => {
              const key = rToCamelCase(value[0] ?? '');
              if (!key) {
                return acc;
              }

              const category = keyToCategoryMap.get(key);
              if (!category) {
                return acc;
              }

              const values = value
                .slice(2) // skip row label (col 0) and Grand Total (col 1)
                .map((v) => Number.parseFloat((v ?? '').replace(/[,%]/g, '')));

              acc[category] = xAxisDates.map((x, index) => ({
                x,
                y: isNaN(values[index]) ? 0 : values[index],
              }));

              return acc;
            },
            { totalTrafficLift: [], widgetUsers: [], additionalPageViews: [] } as typeof series,
          );
        },
      });

      if (parseResult.errors.length) {
        return empty;
      }

      return { columns, rows, series };
    },
  }),
});

export const reportMutations = mutationNamespace(Resource.report, {
  download: mutationOptions({
    mutationFn: async (params: {
      type: string;
      entityType: components['schemas']['ReportEntityTypeEnum'];
      fileName: string;
    }) => {
      const { data, error, response } = await client.GET('/customer/api/tcd/reports/files', {
        params: { query: { type: params.type, entityType: params.entityType, fileName: params.fileName }},
        parseAs: 'blob',
      });

      if (error) {
        throw new HttpException({
          message: `Failed to download report: ${response.statusText}`,
          response,
          isGlobal: false,
        });
      }

      return data;
    },
  }),
});
